import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  Pencil1Icon,
  ReaderIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@radix-ui/react-icons';
import type { BoardPoint, HomeworkAnswerTraceNode } from '@wx-weiqi/contracts';

import { BoardSurface } from './BoardSurface';
import { QuestionRequirementsText } from './QuestionRequirementsText';
import { visibleHomeworkInitialAnnotations } from '../domain/homework-initial-annotations';
import { toggleWorkspaceFullscreen } from '../services/workspace-fullscreen';
import type { StudentActivityApi } from '../services/student-activity';
import type { HomeworkAnswerFrame, HomeworkStore } from '../stores/homework-store';
import { appendHomeworkAnswerTraceNode } from '../domain/homework-answer-trace';
import { homeworkBoardFocus } from '../domain/homework-board-focus';
import {
  activeHomeworkElapsedMs,
  advanceHomeworkCountdownVoice,
  homeworkRemainingSeconds,
  initialHomeworkCountdownVoiceMemory,
} from '../domain/homework-answer-timing';
import { announceHomeworkCountdown, cancelClockAnnouncements } from '../services/clock-voice';
import { useHomeworkTimeout } from './useHomeworkTimeout';
import { nextProblemIndex } from '../domain/homework-answer-progress';

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

export function HomeworkAnswerPage({
  store,
  onBack,
  onComplete,
}: {
  readonly store: HomeworkStore;
  readonly activity?: StudentActivityApi | undefined;
  readonly onBack: () => void;
  readonly onComplete: () => void;
}) {
  const state = useSyncExternalStore(store.subscribe, store.snapshot);
  const assignment = state.selectedAssignment;
  useEffect(() => {
    if (assignment === null) return;
    let refreshing = false;
    const refresh = () => {
      if (document.visibilityState === 'hidden' || refreshing) return;
      refreshing = true;
      void store.refreshSelectedAssignment().finally(() => {
        refreshing = false;
      });
    };
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [assignment?.assignmentId, store]);
  const initialDraft = useRef(
    assignment === null ? null : store.answerDraft(assignment.assignmentId),
  ).current;
  const initialProblemIndex =
    initialDraft?.problemIndex ?? (assignment === null ? 0 : (nextProblemIndex(assignment) ?? 0));
  const [problemIndex, setProblemIndex] = useState(initialProblemIndex);
  const [frames, setFrames] = useState<readonly HomeworkAnswerFrame[]>(initialDraft?.frames ?? []);
  const [traceNodes, setTraceNodes] = useState<readonly HomeworkAnswerTraceNode[]>(
    initialDraft?.traceNodes ?? [],
  );
  const [frameIndex, setFrameIndex] = useState(initialDraft?.frameIndex ?? 0);
  const [elapsedBeforeResumeMs, setElapsedBeforeResumeMs] = useState(initialDraft?.elapsedMs ?? 0);
  const [resumedAtMs, setResumedAtMs] = useState(Date.now());
  const [nowMs, setNowMs] = useState(Date.now());
  const [completed, setCompleted] = useState(
    initialDraft === null && assignment !== null && nextProblemIndex(assignment) === null,
  );
  const [automaticVerdict, setAutomaticVerdict] = useState<'CORRECT' | 'INCORRECT' | null>(null);
  const [automaticResultSaved, setAutomaticResultSaved] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const [fullscreenPending, setFullscreenPending] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [sessionCycle, setSessionCycle] = useState(0);
  const [sessionReady, setSessionReady] = useState(false);
  const [submittedOnServer, setSubmittedOnServer] = useState(false);
  const sessionVersion = useRef(0);
  const checkpointInFlight = useRef(false);
  const pageRef = useRef<HTMLElement | null>(null);
  const automaticSubmissionKey = useRef<string | null>(null);
  const submissionInFlight = useRef(false);
  const advancedProblemKey = useRef<string | null>(null);
  const expiredFrameIndex = useRef<number | null>(null);
  const voiceMemory = useRef(initialHomeworkCountdownVoiceMemory());

  const item = assignment?.items[problemIndex];
  const initialBoard = item?.problem.initialPosition ?? null;
  const currentFrame = frames[frameIndex - 1];
  const board = currentFrame?.board ?? initialBoard;
  const answer = currentFrame?.answer ?? [];
  const lastAnswer = answer.at(-1);
  const activeElapsed =
    assignment === null
      ? 0
      : activeHomeworkElapsedMs(
          elapsedBeforeResumeMs,
          resumedAtMs,
          nowMs,
          assignment.timeLimitSeconds,
        );
  const remainingSeconds =
    assignment === null ? 0 : homeworkRemainingSeconds(assignment.timeLimitSeconds, activeElapsed);
  const problemKey =
    assignment === null || item === undefined
      ? null
      : `${assignment.assignmentId}:${item.problem.problemId}`;
  const timeout = useHomeworkTimeout({
    store,
    cycle: `${problemKey}:${resumedAtMs}`,
    assignmentId: assignment?.assignmentId ?? null,
    problemId: item?.problem.problemId ?? null,
    elapsedLimitMs: (assignment?.timeLimitSeconds ?? 0) * 1_000,
    expired: remainingSeconds === 0,
    enabled:
      sessionReady &&
      !completed &&
      automaticVerdict === null &&
      !submissionInFlight.current &&
      !checkpointInFlight.current,
    pending: state.pending,
    answer,
    nodes: traceNodes,
    submittedNodeIndex: currentFrame?.traceNodeIndex ?? null,
  });
  useEffect(() => {
    if (assignment === null || item === undefined || completed) return;
    let cancelled = false;
    setSessionReady(false);
    setSubmittedOnServer(false);
    void store
      .resumeAnswerSession(assignment.assignmentId, item.problem.problemId)
      .then((response) => {
        if (cancelled) return;
        if (response === null) {
          setSubmittedOnServer(true);
          return;
        }
        const receivedAt = Date.now();
        // The backend enforces the exact deadline; receipt latency must not submit early.
        const elapsed = Math.max(
          0,
          Date.parse(response.serverTimestamp) - Date.parse(response.data.startedAt),
        );
        setElapsedBeforeResumeMs(elapsed);
        setResumedAtMs(receivedAt);
        setNowMs(receivedAt);
        setFrames(response.data.frames);
        setFrameIndex(response.data.frames.length);
        setTraceNodes(response.data.answerTrace.nodes);
        sessionVersion.current = response.data.version;
        expiredFrameIndex.current = null;
        setSessionReady(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Assignment refreshes after a submission must not restart the active question.
  }, [problemKey, sessionCycle, completed, store]);
  useEffect(() => {
    if (sessionReady && remainingSeconds === 0 && expiredFrameIndex.current === null)
      expiredFrameIndex.current = frameIndex;
  }, [remainingSeconds, frameIndex, sessionReady]);
  const boardFocus = useMemo(
    () => (initialBoard === null ? null : homeworkBoardFocus(initialBoard)),
    [initialBoard],
  );

  useEffect(() => {
    if (assignment === null || completed || !sessionReady) return;
    let timer: number | null = null;
    const tick = () => {
      const current = Date.now();
      setNowMs(current);
      const elapsed = activeHomeworkElapsedMs(
        elapsedBeforeResumeMs,
        resumedAtMs,
        current,
        assignment.timeLimitSeconds,
      );
      const remainingMs = assignment.timeLimitSeconds * 1_000 - elapsed;
      if (remainingMs <= 0) return;
      const untilBoundary = remainingMs % 1_000 || 1_000;
      const delay = Math.max(16, Math.min(250, untilBoundary + 4));
      timer = window.setTimeout(tick, delay);
    };
    tick();
    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [assignment, completed, elapsedBeforeResumeMs, resumedAtMs, sessionReady]);

  useEffect(() => {
    if (problemKey === null || completed || !sessionReady) return;
    const advanced = advanceHomeworkCountdownVoice(
      voiceMemory.current,
      problemKey,
      remainingSeconds,
    );
    voiceMemory.current = advanced.memory;
    if (advanced.announcement !== null) announceHomeworkCountdown(advanced.announcement);
  }, [completed, problemKey, remainingSeconds, sessionReady]);

  useEffect(() => () => cancelClockAnnouncements(), []);

  useEffect(() => {
    const synchronize = () => setImmersive(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', synchronize);
    return () => document.removeEventListener('fullscreenchange', synchronize);
  }, []);

  useLayoutEffect(() => {
    const page = pageRef.current;
    if (page === null) return;
    const measure = () => {
      const top =
        document.fullscreenElement === page ? 0 : page.getBoundingClientRect().top + window.scrollY;
      page.style.setProperty(
        '--study-available-height',
        `${Math.max(240, window.innerHeight - top)}px`,
      );
    };
    measure();
    window.addEventListener('resize', measure);
    document.addEventListener('fullscreenchange', measure);
    const observer = new ResizeObserver(measure);
    if (page.previousElementSibling !== null) observer.observe(page.previousElementSibling);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [assignment !== null]);

  useEffect(() => setZoomed(false), [problemKey]);

  useEffect(() => {
    if (!completed) return;
    if (assignment !== null) store.clearAnswerDraft(assignment.assignmentId);
    cancelClockAnnouncements();
    const timer = window.setTimeout(onComplete, 3_000);
    return () => window.clearTimeout(timer);
  }, [assignment, completed, onComplete, store]);

  const moveLabels = useMemo(() => {
    if (board === null) return [];
    const labels = new Map<
      string,
      { readonly point: BoardPoint; readonly label: string; readonly color: 'BLACK' | 'WHITE' }
    >();
    answer.forEach((move, index) => {
      if (move.kind !== 'PLAY') return;
      const color =
        index % 2 === 0
          ? (initialBoard?.currentPlayer ?? 'BLACK')
          : initialBoard?.currentPlayer === 'BLACK'
            ? 'WHITE'
            : 'BLACK';
      if (board.stones[move.point.y * board.size + move.point.x] !== color) return;
      labels.set(`${move.point.x}:${move.point.y}`, {
        point: move.point,
        label: String(index + 1),
        color,
      });
    });
    return [...labels.values()];
  }, [answer, board, initialBoard]);

  if (assignment === null || item === undefined || board === null) {
    return (
      <main className="homework-answer-page">
        <div className="empty-panel">
          <strong>{state.error ?? '作业尚未加载'}</strong>
          <button className="return-button" type="button" onClick={onBack}>
            返回大厅
          </button>
        </div>
      </main>
    );
  }

  const resetForProblem = (nextIndex: number) => {
    const resetAt = Date.now();
    setProblemIndex(nextIndex);
    setSessionReady(false);
    setSubmittedOnServer(false);
    setSessionCycle((cycle) => cycle + 1);
    setFrames([]);
    setTraceNodes([]);
    setFrameIndex(0);
    setElapsedBeforeResumeMs(0);
    setResumedAtMs(resetAt);
    setNowMs(resetAt);
    setAutomaticVerdict(null);
    setAutomaticResultSaved(false);
    automaticSubmissionKey.current = null;
    submissionInFlight.current = false;
    expiredFrameIndex.current = null;
    setZoomed(false);
    store.clearAnswerDraft(assignment.assignmentId);
    store.clearMessages();
  };

  const toggleFullscreen = () => {
    const page = pageRef.current;
    if (page === null || fullscreenPending) return;
    setFullscreenPending(true);
    setFullscreenError(null);
    void toggleWorkspaceFullscreen(page, document)
      .then(setFullscreenError)
      .finally(() => setFullscreenPending(false));
  };

  const play = (point: BoardPoint) => {
    if (
      !sessionReady ||
      checkpointInFlight.current ||
      state.pending !== null ||
      remainingSeconds <= 0 ||
      automaticVerdict !== null
    )
      return;
    checkpointInFlight.current = true;
    const nextAnswer = [...answer, { kind: 'PLAY' as const, point }];
    void store
      .previewAnswer(assignment.assignmentId, item.problem.problemId, nextAnswer)
      .then(async (preview) => {
        // A response that arrives after the deadline must not replace the displayed timeout answer.
        if (
          activeHomeworkElapsedMs(
            elapsedBeforeResumeMs,
            resumedAtMs,
            Date.now(),
            assignment.timeLimitSeconds,
          ) >=
          assignment.timeLimitSeconds * 1_000
        )
          return;
        const trace = appendHomeworkAnswerTraceNode(
          traceNodes,
          currentFrame?.traceNodeIndex ?? null,
          answer,
          preview.answer,
        );
        if (trace === null) return;
        const nextFrames = [
          ...frames.slice(0, frameIndex),
          {
            answer: preview.answer,
            board: preview.board,
            solutionComplete: preview.solutionComplete,
            traceNodeIndex: trace.nodeIndex,
          },
        ];
        const checkpoint = await store.answerSession(
          assignment.assignmentId,
          item.problem.problemId,
          {
            expectedVersion: sessionVersion.current,
            answer: preview.answer,
            answerTrace: {
              nodes: trace.nodes.map((node) => ({
                parentNodeIndex: node.parentNodeIndex,
                moves: [...node.moves],
              })),
              submittedNodeIndex: trace.nodeIndex,
            },
          },
        );
        sessionVersion.current = checkpoint.data.version;
        setTraceNodes(trace.nodes);
        setFrames(nextFrames);
        setFrameIndex(nextFrames.length);
        if (assignment.answerMode === 'AUTOMATIC' && preview.evaluation !== 'CONTINUE') {
          const verdict = preview.evaluation;
          const submissionKey = `${problemKey ?? ''}:${nextFrames.length}:${Date.now()}`;
          automaticSubmissionKey.current = submissionKey;
          setAutomaticVerdict(verdict);
          setAutomaticResultSaved(false);
          void store
            .submitAnswer(
              assignment.assignmentId,
              item.problem.problemId,
              preview.answer,
              {
                nodes: trace.nodes.map((node) => ({
                  parentNodeIndex: node.parentNodeIndex,
                  moves: [...node.moves],
                })),
                submittedNodeIndex: trace.nodeIndex,
              },
              activeHomeworkElapsedMs(
                elapsedBeforeResumeMs,
                resumedAtMs,
                Date.now(),
                assignment.timeLimitSeconds,
              ),
            )
            .then((submission) => {
              if (automaticSubmissionKey.current !== submissionKey) return;
              setAutomaticVerdict(submission.status === 'CORRECT' ? 'CORRECT' : 'INCORRECT');
              setAutomaticResultSaved(true);
              store.clearAnswerDraft(assignment.assignmentId);
            })
            .catch(() => undefined);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        checkpointInFlight.current = false;
        setNowMs(Date.now());
      });
  };

  const navigateFrame = (nextIndex: number) => {
    // After timeout navigation is read-only; the frozen server answer never changes.
    if (remainingSeconds === 0 || automaticResultSaved) {
      setFrameIndex(nextIndex);
      return;
    }
    if (!sessionReady || checkpointInFlight.current || state.pending !== null) return;
    checkpointInFlight.current = true;
    const frame = frames[nextIndex - 1];
    void store
      .answerSession(assignment.assignmentId, item.problem.problemId, {
        expectedVersion: sessionVersion.current,
        answer: [...(frame?.answer ?? [])],
        answerTrace: {
          nodes: traceNodes.map((node) => ({
            parentNodeIndex: node.parentNodeIndex,
            moves: [...node.moves],
          })),
          submittedNodeIndex: frame?.traceNodeIndex ?? null,
        },
      })
      .then((response) => {
        sessionVersion.current = response.data.version;
        setFrameIndex(nextIndex);
      })
      .catch(() => undefined)
      .finally(() => {
        checkpointInFlight.current = false;
        setNowMs(Date.now());
      });
  };

  const advanceToNextProblem = () => {
    // A second activation of the old button must not skip another question.
    if (completed || advancedProblemKey.current === problemKey) return;
    const refreshed = store.snapshot().selectedAssignment;
    if (refreshed === null) return;
    advancedProblemKey.current = problemKey;
    const nextIndex = nextProblemIndex(refreshed, problemIndex + 1);
    if (nextIndex === null) setCompleted(true);
    else resetForProblem(nextIndex);
  };

  const submit = () => {
    if (
      completed ||
      submissionInFlight.current ||
      answer.length === 0 ||
      !sessionReady ||
      checkpointInFlight.current ||
      state.pending !== null ||
      currentFrame === undefined ||
      traceNodes.length === 0
    )
      return;
    submissionInFlight.current = true;
    void store
      .submitAnswer(
        assignment.assignmentId,
        item.problem.problemId,
        answer,
        {
          nodes: traceNodes.map((node) => ({
            parentNodeIndex: node.parentNodeIndex,
            moves: [...node.moves],
          })),
          submittedNodeIndex: currentFrame.traceNodeIndex,
        },
        activeHomeworkElapsedMs(
          elapsedBeforeResumeMs,
          resumedAtMs,
          Date.now(),
          assignment.timeLimitSeconds,
        ),
      )
      .then((submission) => {
        if (assignment.answerMode === 'AUTOMATIC') {
          setAutomaticVerdict(submission.status === 'CORRECT' ? 'CORRECT' : 'INCORRECT');
          setAutomaticResultSaved(true);
          store.clearAnswerDraft(assignment.assignmentId);
          return;
        }
        advanceToNextProblem();
      })
      .catch(() => undefined)
      .finally(() => {
        submissionInFlight.current = false;
        setNowMs(Date.now());
      });
  };

  return (
    <main
      className={`board-workspace board-workspace--student student-play-workspace homework-answer-page warm-study sky-study${immersive ? ' homework-answer-page--immersive' : ''}`}
      ref={pageRef}
    >
      <aside className="student-study-context" aria-label="题目信息">
        <header className="student-study-heading">
          <p className="eyebrow">
            <ReaderIcon aria-hidden="true" /> 我的作业
          </p>
          <div className="study-assignment-title">
            <div>
              <h1>{assignment.title}</h1>
              <span className="student-question-count">
                第 <strong>{problemIndex + 1}</strong> / {assignment.items.length} 题
              </span>
            </div>
            <strong className="homework-first-player">
              <i
                className={initialBoard?.currentPlayer === 'WHITE' ? 'is-white' : ''}
                aria-hidden="true"
              />
              {initialBoard?.currentPlayer === 'WHITE' ? '白先' : '黑先'}
            </strong>
          </div>
          <progress
            aria-label="当前题目进度"
            value={problemIndex + 1}
            max={assignment.items.length}
          />
        </header>
        <section className="student-question-requirements" aria-label="题目要求">
          <h2>
            <ReaderIcon aria-hidden="true" />
            题目要求
          </h2>
          <p>
            <QuestionRequirementsText
              text={item.problem.requirements ?? null}
              richText={item.problem.requirementsRichText ?? null}
              fallback={initialBoard?.currentPlayer === 'WHITE' ? '白先' : '黑先'}
            />
          </p>
        </section>
        <div className="study-context-art" aria-hidden="true">
          <img
            className="study-decoration study-decoration--companions"
            src="/assets/student-study/sky-cat-books.webp"
            alt=""
            draggable={false}
          />
        </div>
      </aside>
      <section className={zoomed ? 'homework-answer-board is-zoomed' : 'homework-answer-board'}>
        <BoardSurface
          boardAppearance="wood"
          size={board.size}
          stones={board.stones}
          initialAnnotations={visibleHomeworkInitialAnnotations(
            item.problem.initialAnnotations,
            sessionReady,
            traceNodes,
          )}
          viewport={zoomed && boardFocus !== null ? boardFocus.viewport : null}
          lastMove={lastAnswer?.kind === 'PLAY' ? lastAnswer.point : null}
          stoneLabels={moveLabels}
          isActionable={(_point, stone) =>
            stone === null &&
            sessionReady &&
            remainingSeconds > 0 &&
            state.pending === null &&
            automaticVerdict === null &&
            currentFrame?.solutionComplete !== true
          }
          onPoint={play}
        />
      </section>
      <aside className="homework-answer-sidebar" aria-label="作答操作">
        <section
          className={`homework-countdown${sessionReady && !submittedOnServer && remainingSeconds <= 10 ? ' is-urgent' : ''}${!sessionReady || submittedOnServer ? ' is-status' : ''}`}
        >
          <ClockIcon className="study-timer-icon" aria-hidden="true" />
          <small>本题剩余时间</small>
          <strong>
            {submittedOnServer
              ? '已提交'
              : sessionReady
                ? formatCountdown(remainingSeconds)
                : state.error !== null
                  ? '同步失败'
                  : '同步计时…'}
          </strong>
          <progress
            aria-label="本题剩余时间比例"
            value={submittedOnServer || !sessionReady ? 0 : remainingSeconds}
            max={assignment.timeLimitSeconds}
          />
          {!submittedOnServer && !sessionReady && state.pending === null ? (
            <button type="button" onClick={() => setSessionCycle((cycle) => cycle + 1)}>
              重试同步
            </button>
          ) : null}
        </section>
        <section className="student-answer-tools" aria-label="答题工具">
          <h2>
            <Pencil1Icon aria-hidden="true" />
            作答操作
          </h2>
          <div className="homework-answer-view-actions" role="group" aria-label="棋盘显示">
            <button
              className="fullscreen-action"
              type="button"
              disabled={fullscreenPending}
              onClick={toggleFullscreen}
            >
              {immersive ? (
                <ExitFullScreenIcon aria-hidden="true" />
              ) : (
                <EnterFullScreenIcon aria-hidden="true" />
              )}
              {immersive ? '退出全屏' : '进入全屏'}
            </button>
            <button
              type="button"
              disabled={boardFocus === null}
              title={boardFocus === null ? '题目不在单一角部，无法放大' : undefined}
              onClick={() => setZoomed((current) => !current)}
            >
              {zoomed ? <ZoomOutIcon aria-hidden="true" /> : <ZoomInIcon aria-hidden="true" />}
              {zoomed ? '恢复全盘' : '棋盘放大'}
            </button>
          </div>
          {fullscreenError !== null ? (
            <p className="message message--error" role="alert">
              {fullscreenError}
            </p>
          ) : null}
          {boardFocus === null ? (
            <small className="homework-board-focus-note">
              题目跨越多个角部或不在单一角部，无法放大
            </small>
          ) : null}
          <div className="homework-answer-progress" role="group" aria-label="作答进度">
            <button
              type="button"
              disabled={frameIndex <= 0}
              onClick={() => navigateFrame(Math.max(0, frameIndex - 1))}
            >
              <ChevronLeftIcon aria-hidden="true" />
              后退
            </button>
            <span>
              {frameIndex} / {frames.length}
            </span>
            <button
              type="button"
              disabled={frameIndex >= frames.length}
              onClick={() => navigateFrame(Math.min(frames.length, frameIndex + 1))}
            >
              前进
              <ChevronRightIcon aria-hidden="true" />
            </button>
          </div>
          {state.error !== null ? <p className="message message--error">{state.error}</p> : null}
          {submittedOnServer ? (
            <p className="homework-timeout-note" role="status">
              本题已提交，请继续下一题。
            </p>
          ) : null}
          {automaticVerdict !== null ? (
            <p
              className={`homework-automatic-result is-${automaticVerdict.toLowerCase()}`}
              role="status"
            >
              <strong>{automaticVerdict === 'CORRECT' ? '作答正确✅' : '作答错误❌'}</strong>
              <span>
                {automaticResultSaved
                  ? '系统已自动完成判定，无需提交给老师批改。'
                  : state.pending === 'submit-answer'
                    ? '正在自动保存判定结果…'
                    : '判定结果尚未保存，请重试。'}
              </span>
            </p>
          ) : null}
          {sessionReady && remainingSeconds === 0 && automaticVerdict === null ? (
            <p className="homework-timeout-note" role="status">
              {timeout.status === 'saved'
                ? '当前题目超时，请继续作答下一题'
                : timeout.status === 'failed'
                  ? '当前题目超时，自动提交失败，请重试。棋盘变化及落子轨迹已保留。'
                  : '当前题目超时，正在自动提交当前变化…'}
            </p>
          ) : null}
        </section>
        <section className="homework-submit-tray" aria-label="提交作答">
          <button
            className="homework-submit-button"
            type="button"
            disabled={
              completed ||
              state.pending !== null ||
              (!submittedOnServer &&
                (!sessionReady ||
                  (remainingSeconds === 0 && automaticVerdict === null
                    ? timeout.status !== 'saved' && timeout.status !== 'failed'
                    : answer.length === 0 && automaticVerdict === null)))
            }
            onClick={
              submittedOnServer
                ? advanceToNextProblem
                : remainingSeconds === 0 && automaticVerdict === null
                  ? timeout.status === 'saved'
                    ? advanceToNextProblem
                    : timeout.retry
                  : automaticVerdict !== null && automaticResultSaved
                    ? advanceToNextProblem
                    : submit
            }
          >
            {submittedOnServer
              ? nextProblemIndex(assignment, problemIndex + 1) === null
                ? '完成作答'
                : '下一题'
              : !sessionReady
                ? '请先同步计时'
                : remainingSeconds === 0 && automaticVerdict === null
                  ? timeout.status === 'saved'
                    ? problemIndex + 1 === assignment.items.length
                      ? '完成作答'
                      : '下一题'
                    : timeout.status === 'failed'
                      ? '重试自动提交'
                      : '正在自动提交…'
                  : state.pending === 'preview-answer'
                    ? '正在判定…'
                    : state.pending === 'submit-answer'
                      ? assignment.answerMode === 'AUTOMATIC'
                        ? '正在保存判定…'
                        : '正在提交…'
                      : automaticVerdict !== null
                        ? automaticResultSaved
                          ? '继续'
                          : '重新保存判定'
                        : problemIndex + 1 === assignment.items.length
                          ? '提交并完成'
                          : '提交并进入下一题'}
          </button>
        </section>
      </aside>
      {completed ? (
        <div className="homework-complete-backdrop" role="status">
          <div>
            <strong>作答完成！</strong>
            <span>{assignment.answerMode === 'AUTOMATIC' ? '自动批改已完成' : '等待老师批改'}</span>
            <small>3s 后自动返回初始界面</small>
          </div>
        </div>
      ) : null}
    </main>
  );
}
